import React, { useCallback, useState, useEffect } from 'react'
import { PiggyWechatForm, PiggyWechat, Branch } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { useRedirectHandleBack, useDialogHandleSubmit, useGetDataByParams } from '../../../utils/default/ComplexFlowHook'
import { Path } from '../../../components/admin/route/route'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import EditFinancePiggyWechatForm, { EditFinancePiggyWechatFormType, initialForm } from '../../../components/admin/finance/EditFinancePiggyWechatForm'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { formatMoney } from '../../../utils/default/TableHelper'

const formToRequest = (form: EditFinancePiggyWechatFormType): Partial<PiggyWechatForm> => ({
  cash_max: Number(form.max),
  cash_min: Number(form.min),
  group_id: form.group === '--' ? null : form.group,
  real_name: form.receiver || null,
  memo: form.memo,
  is_active: form.isActive,
  clients: form.clients,
  layer_ids: form.layer.layers,
  code: form.code,
  qr_content: form.qr || null,
  is_for_proxy_deposit: form.layer.isForProxyDeposit
})

const FinancePiggyWechatUpdatePage: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()
  const [id, setId] = useState<number>(0)
  const [defaultForm, setDefaulForm] = useState<EditFinancePiggyWechatFormType>(initialForm())
  const pageFlow = usePageFlow()
  const globalDialog = useGlobalDialog()
  useGetDataByParams({
    path: Path.FINANCE_PIGGY_WECHAT_UPDATE,
    onBeforeFetch: pageFlow.setLoadingStart,
    gdkFunc: (param: { id: string }) => gdk.finance.getPiggyWechat(Number(param.id)),
    gdkFuncDependencies: [gdk],
    onSuccess: (res: PiggyWechat<number>) => {
      setId(res.id)
      setDefaulForm(initialForm({
        name: res.name,
        account: res.account,
        group: res.group.id ?? '--',
        min: formatMoney(res.cash_min),
        max: formatMoney(res.cash_max),
        receiver: res.real_name,
        memo: res.memo,
        isActive: res.is_active,
        clients: res.clients,
        layer: {
          layers: res.layers.map((item) => item.id),
          isForProxyDeposit: res.is_for_proxy_deposit
        },
        qr: res.qr_content ?? ''
      }))
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: id === 0
  })
  useEffect(() => {
    gdk.trunk.trigger([Branch.PLAYER_LAYER])
  }, [gdk])
  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({ path: Path.FINANCE_PIGGY_WECHAT })
  const { handleSubmit } = useDialogHandleSubmit({
    formToRequest,
    gdkFunc: (payload) => gdk.finance.updatePiggyWechat(id, payload),
    gdkFuncDependencies: [gdk, id],
    dialogId: 'updatePiggyWecht',
    globalDialog,
    getChangeDialogConfig: useCallback((form: EditFinancePiggyWechatFormType) => createGlobalDialogConfig({
      showIcon: false,
      message: (
        <React.Fragment>
          {t('dialog.confirmUpdatePiggyWechat')}
          <span className={classes.purpleWord}>{form.name} {form.receiver}</span>
        </React.Fragment>
      )
    }), [t, classes.purpleWord]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.updatePiggyWechatSuccess')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    afterSuccessDialog: handleBack
  })
  return (
    <Box padding={5}>
      <Paper>
        <Box padding={4}>
          <Box
            paddingY={1.25}
            paddingX={2}
            className={classes.pinkTitleBar}
          >
            <Typography variant="h5">
              {t('page.updatePiggyWechat')}
            </Typography>
          </Box>
          <Box paddingY={2}>
            <LoadingAndErrorFrame { ...pageFlow.status }>
              <EditFinancePiggyWechatForm
                okText={t('common.confirmUpdate')}
                onBack={handleDebouncedBack}
                onSubmit={handleSubmit}
                defaultValue={defaultForm}
                disableName
                disableAccount
              />
            </LoadingAndErrorFrame>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default React.memo(FinancePiggyWechatUpdatePage)
